import { useEffect, useState } from "react";
import axios from "axios";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useSiteData } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20";

export const SeoTab = () => {
  const { settings, refreshSettings } = useSiteData();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!form) setForm({
      seo_title: settings.seo_title,
      seo_description: settings.seo_description,
      ga_measurement_id: settings.ga_measurement_id || "",
      gsc_verification: settings.gsc_verification || "",
      share_image: settings.share_image || "",
    });
  }, [settings, form]);

  if (!form) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.put(`${API}/settings`, form, { withCredentials: true });
      await refreshSettings();
      toast.success("SEO settings saved — live immediately.");
    } catch {
      toast.error("Could not save the SEO settings.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl space-y-6" data-testid="admin-seo-form">
      <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 space-y-5 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
        <h3 className="font-display font-bold text-lg text-[#0b132b]">Search & Sharing</h3>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Page title</span>
          <input data-testid="seo-title-input" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className={`${inputCls} mt-1.5`} />
          <span className="mt-1 block text-[11px] text-slate-400">{form.seo_title.length} characters — aim for under 60.</span>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Meta description</span>
          <textarea data-testid="seo-description-input" rows={3} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className={`${inputCls} mt-1.5 resize-none`} />
          <span className="mt-1 block text-[11px] text-slate-400">{form.seo_description.length} characters — aim for under 160.</span>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Google Analytics 4 — Measurement ID</span>
          <input data-testid="seo-ga-input" value={form.ga_measurement_id} onChange={(e) => setForm({ ...form, ga_measurement_id: e.target.value })} placeholder="G-XXXXXXXXXX" className={`${inputCls} mt-1.5`} />
          <span className="mt-1 block text-[11px] text-slate-400">From analytics.google.com → Admin → Data Streams. Tracking starts as soon as you save.</span>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Google Search Console — Verification token</span>
          <input data-testid="seo-gsc-input" value={form.gsc_verification} onChange={(e) => setForm({ ...form, gsc_verification: e.target.value })} placeholder="Verification token string" className={`${inputCls} mt-1.5`} />
          <span className="mt-1 block text-[11px] text-slate-400">From search.google.com/search-console → Add property → HTML tag method — paste only the token inside content="…".</span>
        </label>
        <p className="text-xs text-slate-400 leading-relaxed">
          Image alt text is edited per image in the Website Editor (hero image) and in the Tours / Destinations tabs. The XML sitemap updates itself automatically at /api/sitemap.xml.
        </p>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Link preview image (photo / logo)</span>
          <input data-testid="seo-share-image-input" value={form.share_image} onChange={(e) => setForm({ ...form, share_image: e.target.value })} placeholder="/api/media/file/… or https://…" className={`${inputCls} mt-1.5`} />
          <span className="mt-1 block text-[11px] text-slate-400">Shown when your link is shared on WhatsApp, Facebook and in Google previews. Upload a photo in the Media tab, copy its link, and paste it here. Leave empty to use the built-in Switzerland image.</span>
        </label>
      </div>

      <div className="rounded-3xl bg-white border border-slate-100 p-6 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Link preview (WhatsApp / Facebook / Google)</p>
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white max-w-md" data-testid="seo-share-preview">
          <img
            src={form.share_image || "/og-image.png"}
            alt="Link preview"
            className="w-full h-44 object-cover"
            data-testid="seo-share-preview-image"
            onError={(e) => { e.currentTarget.src = "/og-image.png"; }}
          />
          <div className="p-3.5 bg-[#f0f2f5]">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">voyage-swiss.com</p>
            <p className="text-sm font-bold text-[#0b132b] leading-snug" data-testid="seo-share-preview-title">Voyage Swiss – Discover Switzerland in Luxury</p>
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">Explore Switzerland with our luxury private tours, family trips, group tours, and VIP chauffeur services.</p>
          </div>
        </div>
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mt-5 mb-3">Google search result</p>
        <div className="rounded-xl border border-slate-100 p-4 bg-[#f8fafc]">
          <p className="text-[#1a0dab] text-lg leading-snug truncate" data-testid="seo-preview-title">{form.seo_title}</p>
          <p className="text-emerald-700 text-xs mt-0.5">voyage-swiss.com</p>
          <p className="text-sm text-slate-600 mt-1 line-clamp-2" data-testid="seo-preview-desc">{form.seo_description}</p>
        </div>
      </div>

      <button
        data-testid="seo-save-button"
        type="submit"
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-7 py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
      >
        <Save className="w-4 h-4" />
        {busy ? "Saving…" : "Save SEO"}
      </button>
    </form>
  );
};
